import { Box, Flex, Image } from '@chakra-ui/react';
import isEmpty from 'lodash/isEmpty';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ImagePlaceholder from 'Asset/image/image-placeholder.svg';
import { AlertOutlineIcon, CopyIcon } from 'Asset/icon';
import BadgeEcomerce from 'Component/badge-ecommerce';
import EmptyState from 'Component/empty-state';
import { Condition, Iteration } from 'Component/others';
import useGetGiveawayBrief from 'Domain/productivity-host/useGetGiveawayBrief';
import formatCurrency from 'Helper/formatCurrency';
import useCopyToClipboard from 'Hook/useCopyToClipboard';
import Accordion from 'UiKit/accordion';
import { TextMedium, TextRegular, TextSemiBold } from 'UiKit/typography';
import { giveawayText } from 'View/productivity-host/const';

import GiveawayContentSkeleton from './skeleton';
import styles from './styles.module.css';

const GiveawayPanel = ({ title }) => {
  return (
    <Box>
      <TextMedium className={styles['panel__text--title']}>{title}</TextMedium>
    </Box>
  );
};

const GiveawayChildren = ({ data }) => {
  const { productName, totalStock, usedStock, accountLivestream, rules, imagePath, title } = data ?? {};

  const isShowMinimumPurchaseText = useMemo(() => rules?.minimumPurchaseAmount > 0, [rules]);
  const requiredProductsText = useMemo(
    () => (isShowMinimumPurchaseText ? giveawayText.requiredProductsWithMinimumPurchaseText : giveawayText.requiredProductsText),
    [isShowMinimumPurchaseText],
  );

  const { copyToClipboard } = useCopyToClipboard();

  const renderRequiredProduct = useMemo(
    () => (product) => {
      const { imagePath, productName, sku } = product;

      const handleCopy = () => copyToClipboard(sku, 'Nomor SKU');

      return (
        <Box className={styles['product__box--product-container']} onClick={handleCopy}>
          <Box className={styles['product__box--product-image-container']}>
            <Image src={imagePath} fallbackSrc={ImagePlaceholder} className={styles['product__box--product-image']} />
          </Box>
          <Box>
            <TextSemiBold className={styles['product__box--product-name']}>{productName}</TextSemiBold>
            <Box className={styles['product__box--product-sku-container']}>
              <TextRegular className={styles['product__box--product-sku']}>{sku}</TextRegular>
              <CopyIcon color="var(--color-primary60)" size="16px" />
            </Box>
          </Box>
        </Box>
      );
    },
    [copyToClipboard],
  );

  const remainingStock = useMemo(() => totalStock - usedStock, [totalStock, usedStock]);
  const formattedMinimumPurchase = useMemo(
    () => (isShowMinimumPurchaseText ? formatCurrency(rules?.minimumPurchaseAmount) : null),
    [isShowMinimumPurchaseText, rules?.minimumPurchaseAmount],
  );

  return (
    <Box>
      <Flex>
        <BadgeEcomerce accountName={accountLivestream?.name} ecommerce={accountLivestream?.ecommerce} />
      </Flex>
      <Box className={styles['giveaway__box--giveaway-product']}>
        <Box className={styles['giveaway__box--giveaway-image-name']}>
          <Box className={styles['giveaway__box--product-image']}>
            <Image src={imagePath} fallbackSrc={ImagePlaceholder} className={styles['giveaway__image-product']} />
          </Box>
          <Box>
            <TextSemiBold className={styles['giveaway__text--product-name']}>{productName}</TextSemiBold>
            <TextRegular className={styles['giveaway__text--giveaway-title']}>{title}</TextRegular>
          </Box>
        </Box>
        <Box className={styles['giveaway__box--stok-giveaway']}>
          <TextRegular className={styles['giveaway__text--stok-label']}>Stok</TextRegular>
          <TextSemiBold className={styles['giveaway__text--stok-value']}>{remainingStock}</TextSemiBold>
        </Box>
      </Box>
      <Box className={styles['giveaway__box--condition-container']}>
        <TextRegular className={styles['giveaway__condition--title']}>{giveawayText.conditionText}</TextRegular>
        <Condition statement={isShowMinimumPurchaseText}>
          <TextMedium className={styles['giveaway__condition--minimum-purchase-text']}>{giveawayText.minimumPurchaseText}</TextMedium>
          <TextSemiBold className={styles['giveaway__condition--minimum-purchase-value']}>{formattedMinimumPurchase}</TextSemiBold>
        </Condition>
        <Condition statement={!isEmpty(rules?.requiredProducts)}>
          <TextMedium className={styles['giveaway__condition--required-product-text']}>{requiredProductsText}</TextMedium>
          <Iteration items={rules?.requiredProducts} render={renderRequiredProduct} />
        </Condition>
        <Condition statement={!isEmpty(rules?.note)}>
          <Box className={styles['giveaway__alert--container']}>
            <AlertOutlineIcon color="var(--color-primary60)" size="12px" />
            <TextRegular className={styles['giveaway__alert--text']}>{rules?.note}</TextRegular>
          </Box>
        </Condition>
      </Box>
    </Box>
  );
};

const GiveawayContent = () => {
  const { t } = useTranslation('empty-state');

  const { data, isLoading } = useGetGiveawayBrief();

  const hasData = useMemo(() => !isEmpty(data?.giveaways), [data]);

  if (isLoading) return <GiveawayContentSkeleton />;

  return (
    <Box className={styles['content__container']}>
      <Condition
        statement={hasData}
        fallback={
          <Box flex={1}>
            <EmptyState
              title={t('specific_item.title', { itemName: 'Giveaway' })}
              description={t('specific_item.description', { itemName: 'Giveaway' })}
              background="var(--color-semantic-white)"
              height="calc(100dvh - 180px)"
            />
          </Box>
        }
      >
        <Iteration
          items={data?.giveaways}
          render={(giveaway, index) => {
            return (
              <Box className={styles['giveaway__box--item']} key={giveaway?.promotionDate || index}>
                <Condition statement={!isEmpty(giveaway?.data)}>
                  <Box className={styles['giveaway__box--date-title']}>
                    <TextRegular className={styles['giveaway__text--date-title']}>{giveaway?.promotionDate}</TextRegular>
                  </Box>
                </Condition>
                <Flex width={'100%'} flexDirection={'column'} gap={'8px'}>
                  <Iteration
                    items={giveaway?.data}
                    render={(timeGroup) => {
                      return (
                        <Accordion
                          header={<GiveawayPanel title={timeGroup?.timeRange} />}
                          children={
                            <Flex flexDirection="column" gap="12px">
                              <Iteration items={timeGroup?.giveaways} render={(item, itemIndex) => <GiveawayChildren data={item} key={itemIndex} />} />
                            </Flex>
                          }
                          key={`${timeGroup?.startTime}-${timeGroup?.endTime}`}
                        />
                      );
                    }}
                  />
                </Flex>
              </Box>
            );
          }}
        />
      </Condition>
    </Box>
  );
};

export default GiveawayContent;
